'use client';

import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthProvider';
import { useUserContext } from '@/contexts/UserProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { track, type AnalyticsProperties } from '@/lib/analytics/track';

/**
 * Hook that enriches in-app events with consistent base properties and fans them
 * out to ALL providers (PostHog + Datadog RUM + GA4) via the unified tracker.
 *
 * Previously this dispatched to PostHog ONLY. It now delegates to
 * `lib/analytics/track.ts`, so every in-app event lands in all three tools with
 * one consistent name + shape, consent-gated. Call sites are unchanged:
 *
 *   const { capture } = useAnalytics();
 *   capture('signal_viewed', { signal_id: '123', signal_type: 'website_visitor' });
 *
 * For events fired OUTSIDE the authenticated app (marketing, auth, onboarding),
 * import `track` directly — those surfaces have no workspace/user context.
 */
export function useAnalytics() {
  const { user } = useAuth();
  const { askIOTUserDetails } = useUserContext();
  const workspaceContext = useWorkspace();

  const capture = useCallback(
    (eventName: string, properties?: AnalyticsProperties) => {
      const baseProperties: AnalyticsProperties = {
        workspace_id: workspaceContext?.activeWorkspace?.vendorid?.toString(),
        workspace_name: workspaceContext?.activeWorkspace?.workspace_name,
        user_id: user?.id || askIOTUserDetails?.user_id,
        user_role: askIOTUserDetails?.role,
        plan_type: workspaceContext?.activeWorkspace?.subscriptionplan,
      };

      track(eventName, { ...baseProperties, ...properties });
    },
    [user, askIOTUserDetails, workspaceContext]
  );

  return { capture };
}
